import { ChangeEvent, FC, useEffect, useState } from 'react';

import postalCodeRegex from '@eneco/common/validation/postalCodeRegex';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { authenticateUser } from '@eneco/customer-insights';
import useDC from '@eneco/dc/useDC';
import { FlowError, StepHeader, Layout, NextButton } from '@eneco/flows/components';
import { useFlowRedirect } from '@eneco/flows/hooks/useFlowRedirect';
import { addressMachine } from '@eneco/flows/machines/index';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { useIsLargeBusinessCheck } from '@eneco/hooks/address/useAddress';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { EnergyUsagePurposeSelector } from '@eneco/sitecore-components-content/AddressFinder/EnergyUsagePurpose/EnergyUsagePurposeSelector';
import { EnergyUsagePurposeListField } from '@eneco/sitecore-components-content/AddressFinder/EnergyUsagePurpose/types';
import { Box, Button, Grid, InputSelect, InputText, Stack } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { SMEAddressStepRendering } from '@sitecore/types/SMEAddressStep';
import { useMachine } from '@xstate/react';
import { useForm } from 'react-hook-form';
import { useDebounce } from 'react-use';
import * as yup from 'yup';

import { getDefaultEnergyUsagePurposeList } from './energy-usage-purpose-fallback';
import { FlowContextSme } from '../../context';

interface FormFields {
  postalCode: string;
  houseNumber: number;
  houseNumberSuffix?: string;
  city: string;
  street: string;
  energyUsagePurpose?: string;
  [key: string]: unknown;
}

export const AddressStep: FC = () => {
  const { useFlowSelector, useFlowActorRef } = useFlowHooks<FlowContextSme>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const { customRedirect } = useFlowRedirect();

  const city = useFlowSelector(state => state.context.city);
  const postalCode = useFlowSelector(state => state.context.postalCode);
  const houseNumber = useFlowSelector(state => state.context.houseNumber);
  const houseNumberSuffix = useFlowSelector(state => state.context.houseNumberSuffix);
  const street = useFlowSelector(state => state.context.street);

  const [addressState, sendToAddressMachine] = useMachine(addressMachine);

  const { SMEAddressStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SMEAddressStep: SMEAddressStepRendering;
  }>();

  const formSchema = yup.object({
    postalCode: yup
      .string()
      .required(textualData?.address?.postalCodeFormField?.value?.requiredMessage)
      .matches(postalCodeRegex, textualData?.address?.postalCodeFormField?.value?.validationMessage),
    houseNumber: yup
      .number()
      .required(textualData?.address?.houseNumberFormField?.value?.requiredMessage)
      .typeError(textualData?.address?.houseNumberFormField?.value?.validationMessage ?? ''),
    houseNumberSuffix: yup.string().optional(),
    street: yup.string().required(),
    city: yup.string().required(),
    energyUsagePurpose: yup
      .string()
      .required("Maak een keuze tussen 'Huishouden' of 'Zakelijk'")
      .notOneOf(['none', ''], "Maak een keuze tussen 'Huishouden' of 'Zakelijk'"),
  });

  const {
    register,
    handleSubmit,
    getFieldState,
    getValues,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitSuccessful, isValidating },
  } = useForm<FormFields>({
    mode: 'onBlur',
    defaultValues: {
      postalCode,
      houseNumber,
      houseNumberSuffix: houseNumberSuffix ?? undefined,
      street,
      city,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const {
    postalCode: postalCodeValue,
    houseNumber: houseNumberValue,
    houseNumberSuffix: houseNumberSuffixValue,
  } = watch();
  const { businessUnit, label } = useDC();
  const { address } = useFormatter();

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const [hasFormValidationErrors, setHasFormValidationErrors] = useState(false);

  const [postCodeToValidate, setPostCodeToValidate] = useState(postalCode);
  const [houseNumberToValidate, setHouseNumberToValidate] = useState(houseNumber);
  const [houseNumberSuffixToValidate, setHouseNumberSuffixToValidate] = useState(houseNumberSuffix);

  const [shouldDisableHouseNumberSuffix, setShouldDisableHouseNumberSuffix] = useState(
    !houseNumberSuffix || !!addressState.context.houseNumberSuffixes,
  );

  useDebounce(
    () => {
      if (postCodeToValidate && houseNumberToValidate) {
        sendToAddressMachine({
          type: 'GET_ADDRESS',
          config: {
            businessUnit,
            label,
          },
          values: {
            postalCode: postCodeToValidate,
            houseNumber: houseNumberToValidate,
          },
        });
      }
    },
    500,
    [postCodeToValidate, houseNumberToValidate],
  );

  useEffect(() => {
    setIsLoading(addressState.matches('FETCHING'));
    setIsSuccessful(addressState.matches('SUCCESS'));
    setIsNotFound(addressState.matches('NOT_FOUND'));
    setIsFailed(addressState.matches('ERROR') || addressState.matches('NOT_FOUND'));

    setShouldDisableHouseNumberSuffix(!addressState.matches('SUCCESS'));

    if (addressState.matches('SUCCESS')) {
      const { street, city } = addressState.context;

      // set values of the hidden fields
      setValue('city', city ?? '');
      setValue('street', street ?? '');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressState]);

  useEffect(() => {
    if (isSubmitSuccessful && isSuccessful) {
      const formData = getValues();

      // TODO: Update redirect link to use Sitecore textualData when available
      if (formData.energyUsagePurpose === 'household') {
        customRedirect('/duurzame-energie/bestellen2/', {
          queryParams: {
            postalCode: addressState.context.postalCode?.replace?.(' ', ''),
            houseNumber: addressState.context.houseNumber?.toString(),
            houseNumberSuffix: formData.houseNumberSuffix,
          },
        });
        return;
      }

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          ...formData,
          houseNumber: Number(formData.houseNumber),
          houseNumberSuffix: formData.houseNumberSuffix || undefined,
          energyUsagePurpose: formData.energyUsagePurpose,
        } as Partial<FlowContextSme>,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  useEffect(() => {
    const { postalCode, houseNumber, houseNumberSuffix } = getValues();

    setHasFormValidationErrors(!(!errors.houseNumber && !errors.postalCode));
    setHouseNumberSuffixToValidate(houseNumberSuffix);

    if (!isValidating && postalCode && houseNumber) {
      if (getFieldState('postalCode').isTouched || getFieldState('houseNumber').isTouched) {
        if (!getFieldState('postalCode').error && !getFieldState('houseNumber').error) {
          if (postalCode !== postCodeToValidate || houseNumber !== houseNumberToValidate) {
            setShouldDisableHouseNumberSuffix(true);

            // reset values of the form fields
            setValue('houseNumberSuffix', '');
            setValue('city', '');
            setValue('street', '');

            // due to use of a "debounce" we update the state right away
            setIsSuccessful(false);
            setIsLoading(true);
            setIsFailed(false);

            setHouseNumberSuffixToValidate('');
          }

          setPostCodeToValidate(postalCode);
          setHouseNumberToValidate(houseNumber);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidating]);

  const isLargeBusiness = useIsLargeBusinessCheck({
    postalCode: postalCodeValue,
    houseNumber: `${houseNumberValue}`,
    houseNumberSuffix: houseNumberSuffixValue,
  });
  const handleEnterpriseRedirect = () => {
    authenticateUser(`zip: ${[postalCode, houseNumber, houseNumberSuffix].join(' ')}`);

    customRedirect(textualData?.enterprise.enterpriseRedirectLink.value.href || '', {
      queryParams: {
        postalCode: postalCodeValue.replace?.(' ', ''),
        houseNumber: houseNumberValue?.toString(),
        houseNumberSuffix: houseNumberSuffixValue || '',
      },
    });
  };

  const nextButton = isLargeBusiness ? (
    <TrackedDialog
      title={textualData?.enterprise.enterpriseDialog.value.title}
      trigger={
        <Stack direction={{ initial: 'column', md: 'row' }}>
          <Button>{textualData?.content?.nextStepText?.value}</Button>
        </Stack>
      }
      width="regular">
      <Box paddingBottom="6">
        <RichText html={textualData?.enterprise.enterpriseDialog.value.content} />
      </Box>
      <Stack direction="row" gap={5}>
        <Button action="primary" type="button" size="regular" onClick={handleEnterpriseRedirect}>
          {textualData?.enterprise.enterpriseDialog.value.submitButtonText}
        </Button>
        <Button
          action="secondary"
          size="regular"
          type="button"
          onClick={() => {
            handleSubmit(() => {})();
          }}>
          {textualData?.enterprise.enterpriseDialog.value.cancelButtonText}
        </Button>
      </Stack>
    </TrackedDialog>
  ) : (
    <NextButton isLoading={isSubmitSuccessful || isLoading}>{textualData?.content?.nextStepText?.value}</NextButton>
  );

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <StepHeader textualData={textualData} />
      <Stack.Item as="fieldset">
        <Grid columns="3" columnGap="3" gridTemplateColumns="2fr 2fr 1.6fr">
          <InputText
            {...register('postalCode', {
              onChange: (event: ChangeEvent<HTMLInputElement>) => {
                const target = event.target;
                const position = target.selectionStart;

                target.value = target?.value?.toUpperCase();
                target.selectionEnd = position;
              },
            })}
            error={errors.postalCode?.message}
            label={textualData?.address?.postalCodeFormField?.value?.label ?? ''}
            placeholder={textualData?.address?.postalCodeFormField?.value?.placeholder}
            hint={textualData?.address?.postalCodeFormField?.value?.hint}
          />

          <InputText
            {...register('houseNumber')}
            error={errors.houseNumber?.message}
            label={textualData?.address?.houseNumberFormField?.value?.label ?? ''}
            placeholder={textualData?.address?.houseNumberFormField?.value?.placeholder}
            hint={textualData?.address?.houseNumberFormField?.value?.hint}
          />

          <InputSelect
            {...register('houseNumberSuffix')}
            label={textualData?.address?.houseNumberSuffixFormField?.value?.label ?? ''}
            placeholder=""
            hint={textualData?.address?.houseNumberSuffixFormField?.value?.hint}
            isDisabled={hasFormValidationErrors || shouldDisableHouseNumberSuffix}
            options={[
              { label: '-', value: '' },
              ...(addressState?.context?.houseNumberSuffixes ?? []).map(suffix => {
                return {
                  label: suffix,
                  value: suffix,
                };
              }),
            ]}
          />
        </Grid>

        {/* TODO: Replace hardcoded energyUsagePurposeList with Sitecore textualData when available */}
        <EnergyUsagePurposeSelector
          control={control}
          energyUsagePurposeList={(() => {
            const address = textualData?.address;
            const addressWithEnergyUsage = address as unknown as {
              energyUsagePurposeList?: EnergyUsagePurposeListField;
            };
            if (addressWithEnergyUsage?.energyUsagePurposeList) {
              return addressWithEnergyUsage.energyUsagePurposeList;
            }
            return getDefaultEnergyUsagePurposeList();
          })()}
          direction="row"
        />

        {!isLoading && !hasFormValidationErrors && (
          <>
            {isSuccessful && (
              <Box paddingTop="2">
                {address.long({
                  street: addressState.context.street,
                  houseNumber: addressState.context.houseNumber,
                  houseNumberSuffix: houseNumberSuffixToValidate,
                  city: addressState.context.city,
                  postalCode: addressState.context.postalCode,
                })}
              </Box>
            )}

            {isFailed && (
              <Box paddingTop="4">
                <FlowError
                  textualData={{
                    notification: isNotFound
                      ? textualData?.notification?.notFoundErrorNotification
                      : textualData?.genericError?.errorNotification,
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Stack.Item>

      <input {...register('city')} type="hidden" />
      <input {...register('street')} type="hidden" />

      {nextButton}
    </Layout.Content>
  );
};
